import { Injectable, inject } from '@angular/core';
import { Observable, map, switchMap } from 'rxjs';
import { API_ENDPOINTS } from '../constants/api-endpoints';
import { ApiResponse } from '../models/api-response.models';
import { CustomerProfile } from '../models/product.models';
import { unwrapApiResponse } from '../utils/api-response';
import { ApiService } from './api.service';

export interface AadhaarOtpResponse {
  referenceId: string;
  maskedAadhaarNumber: string;
  status: string;
  message: string;
}

export interface AadhaarVerifyResponse {
  referenceId: string;
  status: string;
  message: string;
  verifiedName?: string | null;
  verifiedAt: string;
}

@Injectable({ providedIn: 'root' })
export class CustomersService {
  private readonly api = inject(ApiService);

  list(search = ''): Observable<CustomerProfile[]> {
    return this.api
      .get<ApiResponse<{ customers: CustomerProfile[] }> | { customers: CustomerProfile[] }>(
        API_ENDPOINTS.customers.admin,
        { search, includeInactive: false },
        'marketplace',
      )
      .pipe(map((response) => unwrapApiResponse(response).customers ?? []));
  }

  lookupByPhone(phoneNumber: string): Observable<CustomerProfile> {
    const phoneDigits = phoneNumber.replace(/\D/g, '').slice(-10);
    return this.api
      .get<ApiResponse<{ customer: CustomerProfile }> | { customer: CustomerProfile }>(
        API_ENDPOINTS.customers.lookup,
        { phoneNumber: phoneDigits },
        'marketplace',
      )
      .pipe(
        map((response) => unwrapApiResponse(response).customer),
        map((customer) => {
          if (!customer) throw new Error('No active customer was found with this mobile number.');
          return customer;
        }),
      );
  }

  createForSale(payload: {
    phoneNumber: string;
    firstName: string;
    lastName: string;
    email?: string;
  }): Observable<CustomerProfile> {
    return this.api
      .post<any, { phoneNumber: string }>(
        API_ENDPOINTS.customers.createIdentity,
        { phoneNumber: payload.phoneNumber },
        'identity',
      )
      .pipe(
        map(unwrapApiResponse),
        switchMap((identity: any) =>
          this.api.post<any, any>(
            API_ENDPOINTS.customers.admin,
            {
              identityUserId: identity.user?.id,
              phoneNumber: payload.phoneNumber,
              email: payload.email?.trim() || null,
              firstName: payload.firstName.trim(),
              lastName: payload.lastName.trim(),
            },
            'marketplace',
          ),
        ),
        map((response: any) => unwrapApiResponse(response).customer),
      );
  }

  generateAadhaarOtp(
    customerUserId: string,
    aadhaarNumber: string,
  ): Observable<AadhaarOtpResponse> {
    return this.api
      .post<ApiResponse<AadhaarOtpResponse> | AadhaarOtpResponse, { aadhaarNumber: string }>(
        API_ENDPOINTS.customers.aadhaarOtp(customerUserId),
        { aadhaarNumber },
        'marketplace',
      )
      .pipe(map((response) => unwrapApiResponse(response)));
  }

  verifyAadhaarOtp(
    customerUserId: string,
    referenceId: string,
    otp: string,
  ): Observable<AadhaarVerifyResponse> {
    return this.api
      .post<
        ApiResponse<AadhaarVerifyResponse> | AadhaarVerifyResponse,
        { referenceId: string; otp: string }
      >(API_ENDPOINTS.customers.aadhaarVerify(customerUserId), { referenceId, otp }, 'marketplace')
      .pipe(map((response) => unwrapApiResponse(response)));
  }
}
